import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { useTranslation } from 'next-i18next';

import ButtonSecondaryEllipse from '../ButtonSecondaryEllipse/ButtonSecondaryEllipse';
import VideoIcon from '../VideoIcon';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';

import s from './InsightsContactCta.module.scss';

const THEME_PINK_PURPLE = 'pink-purple';
const THEME_PINK_ORANGE = 'pink-orange';
const THEME_PINK_PURPLE_BLUE = 'pink-purple-blue';
const THEME_BLUE_PURPLE = 'blue-purple';
const THEME_BLUE_GREEN = 'blue-green';

const iconMap = {
  [THEME_BLUE_PURPLE]: [
    {
      src: '/video/crown-reveal-hevc/crown--blue-purple--350p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/crown-reveal-vp9/crown--blue-purple--350p--vp9.webm',
      type: 'video/webm',
    },
  ],
  [THEME_PINK_PURPLE]: [
    {
      src: '/video/crown-reveal-hevc/crown--pink-purple--350p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/crown-reveal-vp9/crown--pink-purple--350p--vp9.webm',
      type: 'video/webm',
    },
  ],
  [THEME_PINK_PURPLE_BLUE]: [
    {
      src: '/video/crown-reveal-hevc/crown--pink-purple-blue--350p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/crown-reveal-vp9/crown--pink-purple-blue--350p--vp9.webm',
      type: 'video/webm',
    },
  ],
  [THEME_PINK_ORANGE]: [
    {
      src: '/video/crown-reveal-hevc/crown--pink-orange--350p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/crown-reveal-vp9/crown--pink-orange--350p--vp9.webm',
      type: 'video/webm',
    },
  ],
  [THEME_BLUE_PURPLE]: [
    {
      src: '/video/crown-reveal-hevc/crown--blue-purple--350p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/crown-reveal-vp9/crown--blue-purple--350p--vp9.webm',
      type: 'video/webm',
    },
  ],
  [THEME_BLUE_GREEN]: [
    {
      src: '/video/crown-reveal-hevc/crown--blue-green--350p--hevc.mp4',
      type: 'video/mp4; codecs="hvc1"',
    },
    {
      src: '/video/crown-reveal-vp9/crown--blue-green--350p--vp9.webm',
      type: 'video/webm',
    },
  ],
};

const InsightsContactCta = ({
  title,
  text,
  activityDuration,
  href,
  theme = THEME_BLUE_PURPLE,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { t } = useTranslation();
  const [inViewRef, isVisible] = useIntersectionObserver(0.5, undefined, true);

  const containerCls = classNames(s.Container, {
    [s['Theme--PinkPurpleBlue']]: theme === THEME_PINK_PURPLE_BLUE,
    [s['Theme--PinkPurple']]: theme === THEME_PINK_PURPLE,
    [s['Theme--BlueGreen']]: theme === THEME_BLUE_GREEN,
    [s['Theme--PinkOrange']]: theme === THEME_PINK_ORANGE,
    [s['Theme--BluePurple']]: theme === THEME_BLUE_PURPLE,
  });

  const headingCls = classNames(s.Heading, {
    [s.Visible]: isVisible,
  });
  const textCls = classNames(s.Text, {
    [s.Visible]: isVisible,
  });
  const activityDurationCls = classNames(s.ActivityDuration, {
    [s.Visible]: isVisible,
  });

  let iconSrcs = iconMap?.[theme];

  let url = href?.href;

  // hack to make form show correct dropdown alternative
  if (url?.indexOf('/contact-us/') > -1) {
    url = url + '?type=stockholm-insights';
  }

  return (
    <div className={`${s.Root} grid`}>
      <div
        ref={inViewRef}
        className={containerCls}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <div className={s.CardBorder}></div>
        {url && (
          <Link href={url} legacyBehavior={true}>
            <a className={s.Link}>
              <span className="sr-only">{href?.text || title}</span>
            </a>
          </Link>
        )}

        {iconSrcs && (
          <VideoIcon
            className={s.VideoIcon}
            active={isVisible}
            srcs={iconSrcs}
            animationsDisabledFreezeVideoTime={2}
          />
        )}

        <div className={s.Content}>
          <h3 className={headingCls}>{title}</h3>
          <div className={textCls}>{text}</div>

          <div aria-hidden={true} className={s.DesktopButtonWrap}>
            <ButtonSecondaryEllipse
              size="large"
              active={isHovered}
              tabIndex={-1}
            />
          </div>

          {activityDuration && (
            <div className={activityDurationCls}>
              <Image
                className={s.ActivityIcon}
                width={22}
                height={22}
                src={`/img/clock--white.svg`}
                alt=""
              />

              {activityDuration == 1
                ? t('Generic.activityDuration')
                : t('Generic.activityDurationPlural', {
                    duration: activityDuration,
                  })}
            </div>
          )}

          <div className={s.RespButtonWrap}>
            <ButtonSecondaryEllipse
              size="large"
              active={isHovered}
              tabIndex={-1}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

InsightsContactCta.propTypes = {
  theme: PropTypes.oneOf([
    '',
    THEME_PINK_PURPLE_BLUE,
    THEME_PINK_PURPLE,
    THEME_PINK_ORANGE,
    THEME_BLUE_PURPLE,
    THEME_BLUE_GREEN,
  ]),
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  href: PropTypes.object.isRequired,
  activityDuration: PropTypes.number,
};

InsightsContactCta.defaultProps = {
  theme: THEME_BLUE_PURPLE,
  title: '',
  text: '',
  href: null,
  activityDuration: null,
};

export default InsightsContactCta;
